import axios from "axios";
import Swal from "sweetalert2";
import { baseUrl, defaultAddErrorMessage, errorTranslate } from "../util";

const handleSuccess = ({
  use_swal,
  loading,
  setLoading,
  callback,
  setErrorMessage,
}) => {
  if (use_swal) {
    Swal.fire({
      title: "Success!",
      icon: "success",
      confirmButtonColor: "#a9c1b4",
      color: "#476355",
      background: "#f1f8ef",
    });
  }
  if (loading && setLoading) setLoading("success");
  if (callback) callback();
  if (setErrorMessage) setErrorMessage();
};

const handleError = ({
  error,
  use_swal,
  loading,
  setLoading,
  setErrorMessage,
}) => {
  console.log(error);
  if (loading && setLoading) setLoading("failed");
  if (use_swal) {
    Swal.fire({
      title: errorTranslate(),
      icon: "error",
      text: defaultAddErrorMessage,
      confirmButtonColor: "#a9c1b4",
      color: "#476355",
      background: "#f1f8ef",
    });
    if (setErrorMessage && error?.response?.data) {
      setErrorMessage(error.response.data);
    }
  }
};

const createPackagingPayload = ({ material, qty, supplier, product_code }) =>
  JSON.stringify({
    transport_material: material || null,
    qty: qty || 0,
    supplier: supplier || null,
    product_code: product_code || null,
    input: material || null,
  });

const requestConfig = ({ method, url, data }) => ({
  method,
  url,
  headers: {
    Authorization: "Bearer " + localStorage.getItem("access_token"),
    "Content-Type": "application/json",
  },
  data,
});

/* Flow 31.2a - Transport packaging */
export const getTransportPackagingList = ({
  productId,
  setObjects = () => {},
}) => {
  const config = requestConfig({
    method: "get",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging`,
  });

  axios(config)
    .then((response) => {
      setObjects(response.data.data || []);
    })
    .catch((error) => console.log(error));
};

export const getTransportPackaging = ({
  id: productId,
  componentId,
  setObjects = () => {},
  objects = [],
}) => {
  const config = requestConfig({
    method: "get",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging/${componentId}`,
  });

  axios(config)
    .then((response) => {
      const updatedObjects = objects.map((item) =>
        item.id === componentId ? response.data.data : item
      );
      setObjects(updatedObjects);
    })
    .catch((error) => console.log(error));
};

export const addTransportPackagingToProduct = ({
  productId,
  material,
  qty,
  supplier,
  product_code,
  loading = null,
  setLoading = null,
  use_swal = false,
  callback = null,
  setErrorMessage = () => {},
}) => {
  const config = requestConfig({
    method: "post",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging`,
    data: createPackagingPayload({ material, qty, supplier, product_code }),
  });
  if (loading && setLoading) setLoading("load");

  axios(config)
    .then(() =>
      handleSuccess({
        use_swal,
        loading,
        setLoading,
        callback,
        setErrorMessage,
      })
    )
    .catch((error) =>
      handleError({ error, use_swal, loading, setLoading, setErrorMessage })
    );
};

export const updateTransportPackaging = ({
  productId,
  packagingId,
  material,
  qty,
  supplier,
  product_code,
  objects,
  setObjects = () => {},
  use_swal = false,
  loading = null,
  setLoading = null,
  callback,
  setErrorMessage = () => {},
}) => {
  if (!packagingId) {
    console.error("updateTransportPackaging: packagingId is required but was undefined");
    if (setLoading) setLoading("failed");
    if (setErrorMessage) setErrorMessage("Item ID is missing. Please refresh the page and try again.");
    return;
  }

  const config = requestConfig({
    method: "put",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging/${packagingId}`,
    data: createPackagingPayload({ material, qty, supplier, product_code }),
  });
  if (loading && setLoading) setLoading("load");

  axios(config)
    .then((response) => {
      setObjects(
        objects.map((item) =>
          response.data.data.id === item.id ? response.data.data : item
        )
      );
      handleSuccess({
        use_swal,
        loading,
        setLoading,
        callback,
        setErrorMessage,
      });
    })
    .catch((error) =>
      handleError({ error, use_swal, loading, setLoading, setErrorMessage })
    );
};

export const deleteTransportPackaging = ({
  productId,
  packagingId,
  use_swal = false,
  loading = null,
  setLoading = null,
  callback,
}) => {
  if (!packagingId) {
    console.error("deleteTransportPackaging: packagingId is required but was undefined");
    if (setLoading) setLoading("failed");
    return;
  }

  const config = requestConfig({
    method: "delete",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging/${packagingId}`,
  });
  if (loading && setLoading) setLoading("load");

  axios(config)
    .then(() => {
      handleSuccess({ use_swal, loading, setLoading, callback });
    })
    .catch((error) => console.log(error));
};

/* Flow 31.2b - Transport packaging (outgoing) */
export const getTransportPackagingOutgoingList = ({
  productId,
  setObjects = () => {},
}) => {
  const config = requestConfig({
    method: "get",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging-outgoing`,
  });

  axios(config)
    .then((response) => {
      setObjects(response.data.data || []);
    })
    .catch((error) => console.log(error));
};

export const addTransportPackagingOutgoing = ({
  productId,
  material,
  qty,
  supplier,
  product_code,
  loading = null,
  setLoading = null,
  use_swal = false,
  callback = null,
  setErrorMessage = () => {},
}) => {
  const config = requestConfig({
    method: "post",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging-outgoing`,
    data: createPackagingPayload({ material, qty, supplier, product_code }),
  });
  if (loading && setLoading) setLoading("load");

  axios(config)
    .then(() =>
      handleSuccess({
        use_swal,
        loading,
        setLoading,
        callback,
        setErrorMessage,
      })
    )
    .catch((error) =>
      handleError({ error, use_swal, loading, setLoading, setErrorMessage })
    );
};

export const updateTransportPackagingOutgoing = ({
  productId,
  packagingId,
  material,
  qty,
  supplier,
  product_code,
  objects,
  setObjects = () => {},
  use_swal = false,
  loading = null,
  setLoading = null,
  callback,
  setErrorMessage = () => {},
}) => {
  const config = requestConfig({
    method: "put",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging-outgoing/${packagingId}`,
    data: createPackagingPayload({ material, qty, supplier, product_code }),
  });
  if (loading && setLoading) setLoading("load");

  axios(config)
    .then((response) => {
      setObjects(
        objects.map((item) =>
          response.data.data.id === item.id ? response.data.data : item
        )
      );
      handleSuccess({
        use_swal,
        loading,
        setLoading,
        callback,
        setErrorMessage,
      });
    })
    .catch((error) =>
      handleError({ error, use_swal, loading, setLoading, setErrorMessage })
    );
};

export const deleteTransportPackagingOutgoing = ({
  productId,
  packagingId,
  use_swal = false,
  loading = null,
  setLoading = null,
  callback,
}) => {
  const config = requestConfig({
    method: "delete",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging-outgoing/${packagingId}`,
  });
  if (loading && setLoading) setLoading("load");

  axios(config)
    .then(() => {
      handleSuccess({ use_swal, loading, setLoading, callback });
    })
    .catch((error) => console.log(error));
};

// ============================================
// LCA-SPECIFIC API FUNCTIONS (Flow 31.2a & 31.2b)
// These call /transport-packaging-lca and /transport-packaging-outgoing-lca endpoints
// ============================================

/* Flow 31.2a - Transport packaging LCA */
export const getTransportPackagingListLCA = ({
  productId,
  setObjects = () => {},
}) => {
  const config = requestConfig({
    method: "get",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging-lca`,
  });

  axios(config)
    .then((response) => {
      setObjects(response.data.data || []);
    })
    .catch((error) => console.log(error));
};

export const addTransportPackagingToProductLCA = ({
  productId,
  material,
  qty,
  supplier,
  product_code,
  loading = null,
  setLoading = null,
  use_swal = false,
  callback = null,
  setErrorMessage = () => {},
}) => {
  const config = requestConfig({
    method: "post",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging-lca`,
    data: createPackagingPayload({ material, qty, supplier, product_code }),
  });
  if (loading && setLoading) setLoading("load");

  axios(config)
    .then(() =>
      handleSuccess({
        use_swal,
        loading,
        setLoading,
        callback,
        setErrorMessage,
      })
    )
    .catch((error) =>
      handleError({ error, use_swal, loading, setLoading, setErrorMessage })
    );
};

export const updateTransportPackagingLCA = ({
  productId,
  packagingId,
  material,
  qty,
  supplier,
  product_code,
  objects,
  setObjects = () => {},
  use_swal = false,
  loading = null,
  setLoading = null,
  callback,
  setErrorMessage = () => {},
}) => {
  const config = requestConfig({
    method: "put",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging-lca/${packagingId}`,
    data: createPackagingPayload({ material, qty, supplier, product_code }),
  });
  if (loading && setLoading) setLoading("load");

  axios(config)
    .then((response) => {
      setObjects(
        objects.map((item) =>
          response.data.data.id === item.id ? response.data.data : item
        )
      );
      handleSuccess({
        use_swal,
        loading,
        setLoading,
        callback,
        setErrorMessage,
      });
    })
    .catch((error) =>
      handleError({ error, use_swal, loading, setLoading, setErrorMessage })
    );
};

export const deleteTransportPackagingLCA = ({
  productId,
  packagingId,
  use_swal = false,
  loading = null,
  setLoading = null,
  callback,
}) => {
  const config = requestConfig({
    method: "delete",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging-lca/${packagingId}`,
  });
  if (loading && setLoading) setLoading("load");

  axios(config)
    .then(() => {
      handleSuccess({ use_swal, loading, setLoading, callback });
    })
    .catch((error) => console.log(error));
};

/* Flow 31.2b - Transport packaging outgoing LCA */
export const getTransportPackagingOutgoingListLCA = ({
  productId,
  setObjects = () => {},
}) => {
  const config = requestConfig({
    method: "get",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging-outgoing-lca`,
  });

  axios(config)
    .then((response) => {
      setObjects(response.data.data || []);
    })
    .catch((error) => console.log(error));
};

export const addTransportPackagingOutgoingLCA = ({
  productId,
  material,
  qty,
  supplier,
  product_code,
  loading = null,
  setLoading = null,
  use_swal = false,
  callback = null,
  setErrorMessage = () => {},
}) => {
  const config = requestConfig({
    method: "post",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging-outgoing-lca`,
    data: createPackagingPayload({ material, qty, supplier, product_code }),
  });
  if (loading && setLoading) setLoading("load");

  axios(config)
    .then(() =>
      handleSuccess({
        use_swal,
        loading,
        setLoading,
        callback,
        setErrorMessage,
      })
    )
    .catch((error) =>
      handleError({ error, use_swal, loading, setLoading, setErrorMessage })
    );
};

export const updateTransportPackagingOutgoingLCA = ({
  productId,
  packagingId,
  material,
  qty,
  supplier,
  product_code,
  objects,
  setObjects = () => {},
  use_swal = false,
  loading = null,
  setLoading = null,
  callback,
  setErrorMessage = () => {},
}) => {
  const config = requestConfig({
    method: "put",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging-outgoing-lca/${packagingId}`,
    data: createPackagingPayload({ material, qty, supplier, product_code }),
  });
  if (loading && setLoading) setLoading("load");

  axios(config)
    .then((response) => {
      setObjects(
        objects.map((item) =>
          response.data.data.id === item.id ? response.data.data : item
        )
      );
      handleSuccess({
        use_swal,
        loading,
        setLoading,
        callback,
        setErrorMessage,
      });
    })
    .catch((error) =>
      handleError({ error, use_swal, loading, setLoading, setErrorMessage })
    );
};

export const deleteTransportPackagingOutgoingLCA = ({
  productId,
  packagingId,
  use_swal = false,
  loading = null,
  setLoading = null,
  callback,
}) => {
  const config = requestConfig({
    method: "delete",
    url: `${baseUrl}/api/business/product/${productId}/transport-packaging-outgoing-lca/${packagingId}`,
  });
  if (loading && setLoading) setLoading("load");

  axios(config)
    .then(() => {
      handleSuccess({ use_swal, loading, setLoading, callback });
    })
    .catch((error) => console.log(error));
};
